import type { BadgeVariant } from '../../components/ui/Badge'
import { formatNumber } from '../gingerbread-generator/shapeMetrics'
import { DEFAULT_OUTLINE, formatOutlineWidth } from './outline'
import type { Gingerbread, GingerbreadStatus, JobCounts, JobStatus, Kit, KitCandidate, KitFileKind, KitStatus } from './types'

export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  draft: 'Черновик', processing: 'Обрабатывается', review: 'На приёмке', done: 'Готово', archived: 'В архиве',
}
export const JOB_STATUS_VARIANT: Record<JobStatus, BadgeVariant> = {
  draft: 'gray', processing: 'blue', review: 'yellow', done: 'green', archived: 'gray',
}

export const ITEM_STATUS_LABEL: Record<GingerbreadStatus, string> = {
  pending: 'Ожидает обработки', processing: 'Обрабатывается', ready: 'Готов', failed: 'Ошибка', approved: 'Принят', excluded: 'Исключён',
}
export const ITEM_STATUS_VARIANT: Record<GingerbreadStatus, BadgeVariant> = {
  pending: 'gray', processing: 'blue', ready: 'indigo', failed: 'red', approved: 'green', excluded: 'gray',
}

/** «64,5 × 80 мм» — габарит контура сахарной картинки. */
export const formatSizeMm = (width: number | null | undefined, height: number | null | undefined): string =>
  width == null || height == null ? '—' : `${formatNumber(width, 1)} × ${formatNumber(height, 1)} мм`

/** «51 г» — вес теста, целыми граммами. */
export const formatWeight = (grams: number | null | undefined): string =>
  grams == null || !Number.isFinite(grams) ? '—' : `${formatNumber(grams, 0)} г`

/** Режим формы пряника по-русски: «Контур вокруг рисунка», «Обводка 3 мм» (вырез с белой обводкой) или «Форма: Облако». */
export const shapeModeLabel = (item: Pick<Gingerbread, 'shapeMode' | 'shape' | 'cutMode' | 'outline'>): string => {
  if (item.shapeMode === 'unified') return `Форма: ${item.shape?.name ?? 'не выбрана'}`
  if (item.cutMode === 'outline') return `Обводка ${formatOutlineWidth(item.outline?.widthMm ?? DEFAULT_OUTLINE.widthMm)}`
  return 'Контур вокруг рисунка'
}

/** Название пряника для заголовков и имён файлов. */
export const itemTitle = (item: Pick<Gingerbread, 'name' | 'position'>): string =>
  item.name?.trim() || `Пряник ${item.position}`

/** Принятые из тех, по которым ещё идёт решение: исключённые в знаменатель не входят. */
export const decisionTotals = (counts: JobCounts): { approved: number; of: number } =>
  ({ approved: counts.approved, of: Math.max(0, counts.total - counts.excluded) })

/** Итог по заданию: принято / из скольких и суммарный вес принятых (г). */
export function approvedSummary(items: Gingerbread[]): { approved: number; of: number; weightG: number } {
  const considered = items.filter(item => item.status !== 'excluded')
  const approved = considered.filter(item => item.status === 'approved')
  const weightG = approved.reduce((sum, item) => sum + (item.metrics?.doughWeightG ?? 0), 0)
  return { approved: approved.length, of: considered.length, weightG }
}

/** Пряник, который можно принять одним кликом: готов, свежий и без проблем технологичности. */
export const canBulkApprove = (item: Gingerbread): boolean =>
  item.status === 'ready' && !item.stale && item.manufacturability?.ok !== false

/** Дата и время по-русски. */
export const formatDateTime = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
}

/** Пряник уже выгружен в товары (макет заведён): правка заблокирована сервером (409 GINGERBREAD_EXPORTED_LOCKED). */
export const isExported = (item: Pick<Gingerbread, 'designCode' | 'article'>): boolean => Boolean(item.designCode || item.article)

export const EXPORTED_LOCK_HINT = 'Пряник выгружен в товары — правка заблокирована'

/** Граммы с одним знаком после запятой: «203 г», «66,5 г». */
export const formatGramsOne = (grams: number | null | undefined): string =>
  grams == null || !Number.isFinite(grams) ? '—' : `${formatNumber(grams, 1)} г`

export const KIT_STATUS_LABEL: Record<KitStatus, string> = {
  draft: 'Черновик', rendering: 'Собирается', ready: 'Лист готов', failed: 'Ошибка', exported: 'Выгружен', archived: 'В архиве',
}
export const KIT_STATUS_VARIANT: Record<KitStatus, BadgeVariant> = {
  draft: 'gray', rendering: 'blue', ready: 'green', failed: 'red', exported: 'purple', archived: 'gray',
}

/** «6 шт · нетто 203 г · брутто 303 г». */
export const kitSummaryLine = (kit: Pick<Kit, 'itemsCount' | 'weightNetG' | 'weightGrossG'>): string =>
  `${formatNumber(kit.itemsCount, 0)} шт · нетто ${formatGramsOne(kit.weightNetG)} · брутто ${formatGramsOne(kit.weightGrossG)}`

/** Размер листа набора: «Лист 210 × 310 мм»; пока лист не собран — «—». */
export const kitSheetSize = (kit: Pick<Kit, 'sizeMm'>): string =>
  kit.sizeMm ? `Лист ${formatNumber(kit.sizeMm.widthMm, 1)} × ${formatNumber(kit.sizeMm.heightMm, 1)} мм` : 'Лист не собран'

/** Габарит формы под тесто: «Форма 180 × 200 × 18 мм». */
export const kitFormSize = (kit: Pick<Kit, 'formSizeMm'>): string | null =>
  kit.formSizeMm ? `Форма ${kit.formSizeMm.map(value => formatNumber(value, 1)).join(' × ')} мм` : null

/** Суффикс имени скачиваемого файла набора. */
export const kitFileSuffix: Record<KitFileKind, string> = {
  sheet_jpg: 'sheet.jpg', sheet_plt: 'sheet.plt', cut_jpg: 'cut.jpg', kit_stl: 'kit.stl', preview_jpg: 'preview.jpg',
}

/** Ссылка на карточку товара в «Каталоге товаров». */
export const productHref = (article: string): string => `/products?article=${encodeURIComponent(article)}`

/** Принятый пряник, который можно класть в набор. */
export const canPutInKit = (item: Pick<Gingerbread, 'status'>): boolean => item.status === 'approved'

export const EXPORTED_KIT_HINT = 'Набор выгружен в товары — правка заблокирована'

/** Набор выгружен в товары: макет и товар PP1 заведены. */
export const isKitExported = (kit: Pick<Kit, 'status' | 'article' | 'designCode'>): boolean =>
  kit.status === 'exported' || Boolean(kit.article || kit.designCode)

export const candidateOf = (item: Gingerbread): KitCandidate => ({
  id: item.id, name: itemTitle(item), previewUrl: item.files?.previewPngUrl ?? item.asset.thumbnailUrl, weightG: item.metrics?.doughWeightG ?? null,
})

/** Порядок строк после переноса: элемент `from` встаёт на место `to`. */
export function moveRow<T>(rows: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) return rows
  const next = rows.slice()
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

// ---------------------------------------------------------------- ИИ-генерация рисунков (этап 5)

/** «0,42 $»: доллары с запятой и пробелом перед знаком; у копеечных сумм до трёх знаков. */
export const formatUsd = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value)
    ? '—'
    : `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(value)} $`

/** Предел слотов одного прогона ИИ (гейтвей отвечает 422 GINGERBREAD_AI_PLAN_TOO_LARGE). */
export const AI_MAX_SLOTS = 12

export const AI_RUN_STATUS_LABEL: Record<string, string> = {
  queued: 'В очереди', running: 'Идёт', completed: 'Готово', failed: 'Ошибка', cancelled: 'Отменён',
}
export const AI_RUN_STATUS_VARIANT: Record<string, BadgeVariant> = {
  queued: 'gray', running: 'blue', completed: 'green', failed: 'red', cancelled: 'gray',
}

export const AI_OPERATION_STATUS_LABEL: Record<string, string> = {
  queued: 'В очереди', submitting: 'Отправляется', submitted: 'Отправлена', running: 'Выполняется', uncertain: 'Исход уточняется',
  succeeded: 'Готово', failed: 'Ошибка', cancelled: 'Отменена',
}
export const AI_OPERATION_STATUS_VARIANT: Record<string, BadgeVariant> = {
  queued: 'gray', submitting: 'blue', submitted: 'blue', running: 'blue', uncertain: 'yellow', succeeded: 'green', failed: 'red', cancelled: 'gray',
}

/** Название операции прогона: «Описание рисунков», «Рисование листа 2», «Нарезка листа 2». */
export function aiOperationLabel(operation: { type: string; sheetIndex: number | null; slot: number | null; autoRetry?: boolean }): string {
  const sheet = operation.sheetIndex == null ? '' : ` ${operation.sheetIndex}`
  if (operation.autoRetry) {
    const slot = operation.slot == null ? '' : ` слота ${operation.slot}`
    return operation.type === 'gingerbread_extract' ? `Нарезка автоповтора${slot}` : `Автоповтор${slot}`
  }
  switch (operation.type) {
    case 'gingerbread_text': return 'Описание рисунков'
    case 'gingerbread_sheet': return `Рисование листа${sheet}`
    case 'gingerbread_item': return 'Перерисовка пряника'
    case 'gingerbread_extract': return `Нарезка листа${sheet}`
    default: return operation.type
  }
}

/** Прогон ещё работает: операции в очереди или идут. */
export const isAiRunActive = (run: { status: string }): boolean => run.status === 'queued' || run.status === 'running'

/**
 * Операции прогона для экрана. Автоповтор, который не понадобился (надпись не у края), отменяется без расхода: такие
 * «ворота» не показываются и сбоем не считаются (гейтвей их уже скрывает, фильтр — страховка).
 */
export const visibleAiOperations = <T extends { status: string; autoRetry?: boolean }>(operations: T[]): T[] =>
  operations.filter(operation => !(operation.autoRetry && operation.status === 'cancelled'))

/** Сколько операций прогона закончено (успешно, с ошибкой или отменено) из скольких. */
export const aiRunProgress = (run: { operations: Array<{ status: string; autoRetry?: boolean }> }): { done: number; total: number } => {
  const operations = visibleAiOperations(run.operations)
  return {
    done: operations.filter(operation => ['succeeded', 'failed', 'cancelled'].includes(operation.status)).length,
    total: operations.length,
  }
}

/** Предупреждение «надпись у края формы» у пряника ИИ (первое подходящее) либо undefined. */
export const edgeTextWarning = (item: { source: string; aiWarnings?: Array<{ code: string; message: string; autoRetry?: boolean; suggestRegenerate?: boolean }> }) =>
  item.source === 'ai' ? item.aiWarnings?.find(warning => warning.code === 'AI_TEXT_NEAR_EDGE') : undefined

/** Код предупреждения проверки надписей листа: надпись на прянике не совпала с задуманной. */
export const INSCRIPTION_MISMATCH_CODE = 'AI_TEXT_MISMATCH'

/** Предупреждение «надпись не совпала» у пряника ИИ (первое подходящее) либо undefined. */
export const inscriptionMismatchWarning = (item: { source: string; aiWarnings?: Array<{ code: string; message: string; expected?: string; found?: string }> }) =>
  item.source === 'ai' ? item.aiWarnings?.find(warning => warning.code === INSCRIPTION_MISMATCH_CODE) : undefined

/** Текст отметки: «Надпись не совпала: ожидалось «…», на прянике «…»»; лишний текст на пряничке без надписи - своей фразой. */
export const inscriptionMismatchText = (warning: { message: string; expected?: string; found?: string }): string => {
  const expected = (warning.expected ?? '').trim()
  const found = (warning.found ?? '').trim()
  if (!expected && found) return `Лишний текст на прянике: «${found}»`
  if (expected) return `Надпись не совпала: ожидалось «${expected}», ${found ? `на прянике «${found}»` : 'на прянике надписи нет'}`
  return warning.message || 'Надпись не совпала с задуманной'
}

/** Ошибки гейтвея, после которых смета недействительна и план нужно рассчитать заново. */
export const AI_QUOTE_ERROR_CODES: ReadonlySet<string> = new Set(['QUOTE_INVALID', 'QUOTE_CONFLICT', 'QUOTE_EXPIRED'])
